// @ts-nocheck
import get from "lodash.get";
import moment from "moment";
import CommonUtils from "services/common/utils";
import turf from "turf";

class MapUtils {
  static getDefaultCenter = (mapState) => {
    const position = get(mapState, "config.default_position", undefined);

    if (!position) {
      return null;
    }

    return MapUtils.getNormalizedCoordinates(position);
  };

  static getDefaultCatchmentAreasCenter = (mapState) => {
    const position = get(
      mapState,
      "config.visitor_characteristics.default_position",
      undefined
    );

    if (!position) {
      return null;
    }

    return MapUtils.getNormalizedCoordinates(position);
  };

  static getCenter = (mapState) => {
    const currentPosition = get(mapState, "config.currentPosition", undefined);

    if (!currentPosition) {
      return MapUtils.getDefaultCenter(mapState);
    }

    return MapUtils.getNormalizedCoordinates(currentPosition);
  };

  static getCatchementAreasCenter = (mapState) => {
    const currentPosition = get(mapState, "config.currentPosition", undefined);

    if (!currentPosition) {
      return MapUtils.getDefaultCatchmentAreasCenter(mapState);
    }

    return MapUtils.getNormalizedCoordinates(currentPosition);
  };

  static getIndicatorsTilesUrl = (mapState) =>
    get(
      mapState,
      "config.tiles_url_indicators",
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    );

  static getDefaultZoom = (mapState) => {
    return get(mapState, "config.default_zoom", undefined);
  };

  static getHeatmapOccupancy = (mapState) => {
    return get(mapState, "config.heatmap_occupancy", undefined);
  };

  static getMinZoom = (mapState) => {
    return get(mapState, "config.min_zoom_allowed", undefined);
  };

  static getMaxZoom = (mapState) => {
    return get(mapState, "config.max_zoom_allowed", undefined);
  };

  static getZoom = (mapState) => {
    return get(
      mapState,
      "config.currentZoom",
      MapUtils.getDefaultZoom(mapState)
    );
  };

  static getFloor = (mapState) => {
    return get(mapState, "config.currentFloor");
  };

  static getTilesUrl = (mapState) => {
    const currentFloor = get(mapState, "config.currentFloor", 0);
    let tilesUrl = get(mapState, "config.tiles_url", undefined);

    if (tilesUrl && currentFloor !== undefined) {
      tilesUrl = tilesUrl.replace("{floor}", currentFloor || "0");
    }

    return tilesUrl;
  };

  static getNormalizedCoordinates = (coordinates): [number, number] => {
    if (!coordinates) {
      return null;
    }

    return [coordinates?.lat, coordinates?.lng];
  };

  static getNormalizedAreaCoordinates = (coordinates): [] => {
    if (!coordinates || !Array.isArray(coordinates)) {
      return null;
    }

    return coordinates.map((singleCoord) =>
      MapUtils.getNormalizedCoordinates(singleCoord)
    );
  };

  static getNormalizedLineCoordinates = (start, end) => {
    if (!start || !end) {
      return null;
    }

    return [
      MapUtils.getNormalizedCoordinates(start),
      MapUtils.getNormalizedCoordinates(end),
    ];
  };

  static getRadiusInMeters = (firstPoint, secondPoint) => {
    return Math.round(firstPoint.distanceTo(secondPoint));
  };

  static toggleHeatmap = (timestamp, maxDaysAllowed) =>
    moment().startOf("day").subtract(maxDaysAllowed, "d") >
    moment(timestamp * 1000);

  static toggleMarkersAndAreas = (timestamp, maxDaysAllowed) => {
    return (
      moment().startOf("day").subtract(maxDaysAllowed, "d") <=
      moment(timestamp * 1000)
    );
  };

  static formatConfigFromApi = (config) => {
    return {
      ...config,
      flow: MapUtils.formatFlowConfigFromApi(get(config, "flow", {})),
    };
  };

  static formatFlowConfigFromApi = (flowConfig) => {
    CommonUtils.resetColorFactoryIndex();

    return {
      ...flowConfig,
      areas: get(flowConfig, "areas", []),
      points: get(flowConfig, "points", []).map((pointConfig) => ({
        ...pointConfig,
        display: {
          ...get(pointConfig, "display", {}),
          selection_color: get(
            pointConfig,
            "display.selection_color",
            CommonUtils.getNewColor()
          ),
        },
      })),
      paths: get(flowConfig, "paths", []),
    };
  };

  static getCenterOfPolygon = (polygonCoords) => {
    const polygon = turf.polygon([
      [...polygonCoords.map((coord) => [coord.lng, coord.lat])],
    ]);

    const centroid = turf.centroid(polygon);

    return centroid.geometry.coordinates;
  };

  static getCenterOfPoints = (points) => {
    if (points.length === 0) {
      return [];
    }

    if (points.flat().length === 0) {
      return [];
    }

    const features = points
      .map((point) => {
        if (point.length <= 1) {
          return null;
        }

        return turf.point(point);
      })
      .filter((point) => point !== null);

    const center = turf.center(turf.featureCollection(features));

    return center.geometry.coordinates.reverse();
  };
}

export default MapUtils;
